/**
 * RigheRiepilogo — Scheda V3
 *
 * Tre righe collassabili (Foto / Costi / Pagamenti) che mostrano uno stato
 * sintetico e ospitano i pannelli esistenti quando aperte.
 *
 * Regole:
 * - Se l'utente non ha il permesso di lettura, la riga NON compare.
 * - Se la sezione ha contenuto, la riga nasce APERTA.
 * - I pannelli (PhotoGallery, CostiPanel, PagamentiPanel) sono importati
 *   e riusati così come sono — nessuna riscrittura.
 *
 * NOTA: importa CostiPanel e PagamentiPanel da scheda-v2. I componenti
 * condivisi dovrebbero stare in components/shared (ticket W37).
 */
import React, { useState } from 'react'
import { theme, Typography } from 'antd'
import {
  CaretDownOutlined,
  CaretRightOutlined,
  DollarOutlined,
  FileImageOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { usePermission } from '@/hooks/usePermission'
import { useCostItemsForWorkOrder } from '@/hooks/useCostItems'
import { usePaymentsForWorkOrder } from '@/hooks/usePayments'
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import CostiPanel from '@/components/scheda-v2/CostiPanel'
import PagamentiPanel from '@/components/scheda-v2/PagamentiPanel'
import { PhotoGallery } from '@/components/foto/PhotoGallery'
import type { Intervention } from '@/types'

const { Text } = Typography

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Formatta un importo numerico come valuta italiana. */
function fmtEur(val: string | number | null | undefined): string {
  const n = typeof val === 'string' ? parseFloat(val) : (val ?? null)
  if (n == null || isNaN(n)) return '0,00 €'
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
}

// ── Riga collassabile generica ────────────────────────────────────────────────

interface RigaProps {
  icon: React.ReactNode
  label: string
  summary: React.ReactNode
  defaultOpen: boolean
  children: React.ReactNode
}

const Riga: React.FC<RigaProps> = ({ icon, label, summary, defaultOpen, children }) => {
  const { token } = theme.useToken()
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        marginTop: 8,
        overflow: 'hidden',
      }}
    >
      {/* ── Header riga (sempre visibile, ~44px) ─── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '10px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          gap: 8,
          minHeight: 44,
          textAlign: 'left',
        }}
      >
        <span style={{ color: token.colorTextSecondary, fontSize: 14 }}>{icon}</span>

        <Text strong style={{ color: token.colorText, fontSize: 14, flex: 1 }}>
          {label}
        </Text>

        <Text type="secondary" style={{ fontSize: 13, marginRight: 8 }}>
          {summary}
        </Text>

        <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
          {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </span>
      </button>

      {/* ── Contenuto espanso ─── */}
      {open && (
        <div style={{
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: '12px 16px',
        }}>
          {children}
        </div>
      )}
    </div>
  )
}

// ── Props del componente ──────────────────────────────────────────────────────

interface Props {
  workOrderId: number
  interventions?: Intervention[]
  isNew?: boolean
}

// ── Componente principale ─────────────────────────────────────────────────────

const RigheRiepilogo: React.FC<Props> = ({ workOrderId, interventions = [], isNew = false }) => {
  const canViewPhotos   = usePermission('work_order_photos.view')
  const canViewCosts    = usePermission('costs.view')
  const canViewPayments = usePermission('payments.view')

  // ── Dati foto (per sapere se ci sono foto e la loro ripartizione per fase) ────
  // Stessa queryKey di PhotoGallery → TanStack Query condivide la cache: una sola
  // chiamata HTTP reale. Il backend restituisce già il campo `fase` nell'endpoint.
  const { data: photos = [] } = useQuery({
    queryKey: ['work-order-photos', workOrderId],
    queryFn: async () => {
      const res = await axiosInstance.get<{ id: number; fase: string }[]>(`/work-orders/${workOrderId}/photos`)
      return res.data
    },
    enabled: canViewPhotos && !isNew && workOrderId > 0,
  })

  // ── Dati costi ───────────────────────────────────────────────────────────────
  const { data: costiData } = useCostItemsForWorkOrder(
    canViewCosts ? workOrderId : undefined
  )

  // ── Dati pagamenti ───────────────────────────────────────────────────────────
  const { data: paghData } = usePaymentsForWorkOrder(
    canViewPayments ? workOrderId : undefined
  )

  // ── Summary sintetici ────────────────────────────────────────────────────────

  const fotoCount = photos.length

  // Ripartizione per fase — ordine canonico da PhotoGallery (non ridefinito: stesso array)
  const FASI_FOTO = ['accettazione', 'lavorazione', 'riconsegna'] as const
  const fazeCounts = FASI_FOTO.reduce<Record<string, number>>((acc, f) => {
    acc[f] = photos.filter(p => p.fase === f).length
    return acc
  }, {})
  const fasiConFoto = FASI_FOTO.filter(f => fazeCounts[f] > 0)

  const fotoSummary: React.ReactNode = fotoCount === 0
    ? <span>nessuna caricata</span>
    : fasiConFoto.length > 1
    ? <>
        <strong>{fotoCount}</strong>
        {' · '}
        {fasiConFoto.map(f => `${fazeCounts[f]} ${f}`).join(', ')}
      </>
    : <strong>{fotoCount}</strong>

  const costiTotale = costiData?.totali?.totale_documento
  const costiHasContent = costiData != null && costiData.items.length > 0
  const costiSummary = costiHasContent
    ? <><strong>{fmtEur(costiTotale)}</strong></>
    : <span>scheda non ancora quotata</span>

  const paghIncassato  = paghData?.riepilogo?.totale_incassato
  const paghDovuto     = paghData?.riepilogo?.importo_dovuto
  const paghHasContent = paghData != null && paghData.items.length > 0
  const paghSummary = paghHasContent
    ? <>
        <strong>{fmtEur(paghIncassato)}</strong>
        {paghDovuto != null && <> su {fmtEur(paghDovuto)}</>}
      </>
    : <span>nessuno registrato</span>

  return (
    <div style={{ marginTop: 4 }}>
      {/* ── FOTO ─── */}
      {canViewPhotos && !isNew && (
        <Riga
          icon={<FileImageOutlined />}
          label="Foto"
          summary={fotoSummary}
          defaultOpen={fotoCount > 0}
        >
          <PhotoGallery
            workOrderId={workOrderId}
            interventions={interventions}
            collapsible={false}
          />
        </Riga>
      )}

      {/* ── COSTI ─── */}
      {canViewCosts && (
        <Riga
          icon={<DollarOutlined />}
          label="Costi"
          summary={costiSummary}
          defaultOpen={costiHasContent}
        >
          <CostiPanel workOrderId={workOrderId} collapsible={false} />
        </Riga>
      )}

      {/* ── PAGAMENTI ─── */}
      {canViewPayments && (
        <Riga
          icon={<WalletOutlined />}
          label="Pagamenti"
          summary={paghSummary}
          defaultOpen={paghHasContent}
        >
          <PagamentiPanel workOrderId={workOrderId} collapsible={false} />
        </Riga>
      )}
    </div>
  )
}

export default RigheRiepilogo
